import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { CheckCircle, ArrowLeft, Mail, Phone, Calendar } from 'lucide-react';
import { servicesData } from '../../utils/servicesData';
import { useSiteContent } from '../../hooks/useSiteContent';
import T from '../T';

const ServiceDetailPage: React.FC = () => {
  const { serviceId } = useParams<{ serviceId: string }>();
  const navigate = useNavigate();
  const { content } = useSiteContent();

  const editableService = serviceId ? content.services.find((item) => item.id === serviceId) : null;
  const fallbackService = serviceId ? servicesData[serviceId] : null;

  const service = editableService
    ? {
        id: editableService.id,
        title: editableService.pageTitle,
        description: editableService.pageDescription,
        fullContent: editableService.pageContent,
        features: editableService.featureList
          .split(/\r?\n/)
          .map((line) => line.trim())
          .filter(Boolean),
        image: editableService.image,
        ctaText: editableService.ctaText,
      }
    : fallbackService;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!service) {
    return (
      <div className="container section-padding" style={{ textAlign: 'center', minHeight: '60vh', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <h2><T>Service not found</T></h2>
        <p><T>The legal service you are looking for does not exist.</T></p>
        <Link to="/" className="btn btn-primary" style={{ alignSelf: 'center', marginTop: '20px' }}>
          <T>Back to Home</T>
        </Link>
      </div>
    );
  }

  return (
    <div className="service-detail-page" style={{ backgroundColor: 'var(--bg-color)', color: 'var(--text-color)', minHeight: '100vh' }}>
      {/* Hero Section */}
      <section 
        className="hero-section" 
        style={{ 
          height: '400px', 
          backgroundImage: `linear-gradient(rgba(10, 12, 16, 0.7), rgba(10, 12, 16, 0.7)), url(${service.image})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          textAlign: 'center',
          borderBottom: '1px solid rgba(0, 212, 255, 0.3)'
        }}
      >
        <div className="container">
          <h1 style={{ fontSize: '3.5rem', marginBottom: '1rem', fontWeight: 'bold', color: '#E0E0E0', textShadow: '0 0 15px rgba(0, 212, 255, 0.6)' }}>
            <T>{service.title}</T>
          </h1>
          <p style={{ fontSize: '1.2rem', maxWidth: '700px', margin: '0 auto', color: '#ccc' }}>
            <T>{service.description}</T>
          </p>
        </div>
      </section>

      <section className="section-padding">
        <div className="container">
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--primary-color)', textDecoration: 'none', marginBottom: '30px' }}>
            <ArrowLeft size={18} /> <T>Back to Services</T>
          </Link>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '4rem' }}>
            <div>
              <h2 style={{ color: 'var(--primary-color)', marginBottom: '1.5rem', fontSize: '2rem' }}>
                <T>Overview</T>
              </h2>
              <div style={{ lineHeight: '1.8', fontSize: '1.1rem', color: '#000000', marginBottom: '2rem', whiteSpace: 'pre-wrap' }}>
                <T>{service.fullContent}</T>
              </div>

              <h3 style={{ color: 'var(--primary-color)', marginBottom: '1.2rem' }}>
                <T>Key Areas of Expertise</T>
              </h3>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {service.features.map((feature, index) => (
                  <li key={index} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '15px', color: '#000000' }}>
                    <CheckCircle size={20} color="var(--secondary-color)" style={{ marginTop: '3px', flexShrink: 0 }} />
                    <span><T>{feature}</T></span>
                  </li>
                ))}
              </ul>
            </div>

            <div style={{ position: 'sticky', top: '100px', height: 'fit-content' }}>
              <div style={{ backgroundColor: 'rgba(255, 255, 255, 0.8)', padding: '40px', borderRadius: '12px', boxShadow: 'var(--shadow)', border: '1px solid var(--primary-color)' }}>
                <h3 style={{ color: 'var(--primary-color)', marginBottom: '1.5rem', textAlign: 'center' }}>
                  <T>Need Professional Assistance?</T>
                </h3>
                <p style={{ textAlign: 'center', color: '#333', marginBottom: '2rem' }}>
                  <T>Our award-winning legal team is ready to help you navigate your legal journey with precision and care.</T>
                </p>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', marginBottom: '25px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '15px', backgroundColor: 'rgba(255, 255, 255, 0.9)', borderRadius: '8px' }}>
                    <Phone size={20} color="var(--primary-color)" />
                    <div>
                      <div style={{ fontSize: '0.8rem', color: '#555' }}><T>Call Us</T></div>
                      <div style={{ fontWeight: 'bold', color: 'black' }}>+1 (905) 604-5521</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '15px', backgroundColor: 'rgba(255, 255, 255, 0.9)', borderRadius: '8px' }}>
                    <Mail size={20} color="var(--primary-color)" />
                    <div>
                      <div style={{ fontSize: '0.8rem', color: '#555' }}><T>Email Us</T></div>
                      <div style={{ fontWeight: 'bold', color: 'black' }}>lawyer@yliu.ca</div>
                    </div>
                  </div>
                </div>

                <Link to="/intake" className="btn btn-primary" style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', padding: '15px' }}>
                  <Calendar size={18} /> <T>{service.ctaText}</T>
                </Link>
                <div style={{ textAlign: 'center', marginTop: '15px' }}>
                  <Link to="/contact" style={{ color: 'var(--secondary-color)', fontSize: '0.9rem', fontWeight: 'bold', textDecoration: 'none' }}>
                    <T>Or visit our Contact Page</T>
                  </Link>
                </div>
              </div>
              
              <div style={{ marginTop: '30px', borderRadius: '12px', overflow: 'hidden', boxShadow: 'var(--shadow)', border: '1px solid var(--primary-color)' }}>
                <img 
                  src={service.image}
                  alt={service.title}
                  style={{ width: '100%', height: 'auto', display: 'block' }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section style={{ backgroundColor: 'var(--primary-color)', color: 'white', padding: '60px 0' }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <h2 style={{ marginBottom: '1.5rem' }}><T>Bridging Legal Cultures</T></h2>
          <p style={{ maxWidth: '800px', margin: '0 auto', opacity: 0.9, lineHeight: 1.6 }}>
            <T>As a bilateral firm with presence in Toronto and Beijing, we provide unique insights that conventional firms cannot match. Your legal matters are handled by experts who understand both jurisdictions perfectly.</T>
          </p>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetailPage;
